// АНИМ.1: новый Animator для боевых анимаций.
//
// Полная переделка с нуля по спеке (АНИМ v1.0). Заменяет старые
// BattleAnimator + phase3-animator с накопленными костылями.
//
// API совместимый с engine (src/engine/phases.js):
//   await anim.revealCard(card)
//   await anim.beforeAttack(attacker, target)
//   await anim.afterAttack(attacker, target, atkDmg, defDmg)
//   await anim.death(card)
//   await anim.heroDeath(hero)
//   await anim.pause()
//   anim.rerender()
//   anim.shouldStop()
//   anim.rewindAttack()  ← убирает ghost если attack_start без impact
//
// Архитектура:
//   - PvE: engine между мутациями state'а вызывает методы async. Между
//     ними anim.rerender() форсит React-rerender. Внутри методов мы
//     ставим overlay-события (current) и нотифицируем подписчиков.
//   - PvP: AnimRecorder (server) пишет события в массив, AnimReplay
//     (client) проигрывает через этот же Animator.

/* eslint-disable @typescript-eslint/no-explicit-any */

// ─── Тайминги (мс) ─────────────────────────────────────────
export const T = {
  reveal: 350,
  stackUp: 500,
  keywordFlash: 550,
  phaseBanner: 900,
  cardDraw: 550,
  cardPlacement: 650,
  attackTotal: 850,        // unit→unit
  attackHero: 950,         // unit→hero
  heroAttack: 1100,        // hero→anything
  telegraph: 100,
  lunge: 350,
  impact: 100,
  damageNumbers: 250,
  returnMs: 300,
  death: 550,
  heroDeath: 1500,
  pause: 250,
  immune: 600,
  parachute: 800,
  redirectArrow: 300,
  raschet: 500,
  stealth: 500,
  heal: 500,
  buff: 500,
  summon: 600,
  playAction: 1200,
  move: 600,
  influence: 1200,
};

// АНИМ.6: новые типы overlay-событий для spec-эффектов.

// ─── Типы событий overlay'я ───────────────────────────────
export interface CardSnapshot {
  instanceId: number;
  id: string;
  name_ru: string;
  faction: string;
  atk: number; armor: number; hp: number; maxHp: number;
  isHero: boolean;
}

export interface RectInfo {
  left: number; top: number; width: number; height: number;
}

export type OverlayEvent =
  | { kind: 'reveal'; instanceId: number; rect: RectInfo }
  | { kind: 'attack';
      attacker: CardSnapshot;
      target: CardSnapshot;
      attackerRect: RectInfo;
      targetRect: RectInfo;
      stage: 'telegraph' | 'lunge' | 'impact' | 'return';
      atkDmg: number | null;
      defDmg: number | null;
    }
  | { kind: 'death'; instanceId: number; rect: RectInfo; discardRect: RectInfo | null;
      snap: CardSnapshot | null }
  | { kind: 'heroDeath'; ownerIdx: 0 | 1; rect: RectInfo }
  // АНИМ.6: золотой щит на Hero при блокированной атаке.
  | { kind: 'heroImmune'; ownerIdx: 0 | 1; rect: RectInfo }
  // АНИМ.6: белая вспышка вокруг стелс-юнита в момент активации.
  | { kind: 'stealthActivate'; instanceId: number; rect: RectInfo }
  // АНИМ.7: разыгрывается карта-Действие.
  | { kind: 'playAction';
      cardId: string;
      cardName: string;
      faction: string;
      targetRect: RectInfo | null;
    }
  // АНИМ.7: +HP юниту.
  | { kind: 'heal'; instanceId: number; rect: RectInfo; amount: number }
  // АНИМ.7: +ATK/+ARM/+HP юниту.
  | { kind: 'buff'; instanceId: number; rect: RectInfo;
      dAtk: number; dArmor: number; dHp: number }
  // АНИМ.7: призыв юнита на поле.
  | { kind: 'summon'; instanceId: number; rect: RectInfo }
  // АНИМ.7: перенаправление атаки (стрелка).
  | { kind: 'redirect';
      fromRect: RectInfo;
      toRect: RectInfo;
      reason: 'sekretar' | 'taunt';
    }
  // АНИМ.7: урон без attacker'а (Огневой вызов и т.п.).
  | { kind: 'damage';
      instanceId: number;
      rect: RectInfo;
      amount: number;
      isHero: boolean;
    }
  // АНИМ.8: Парашют — Hero спасается от смертельного урона на 1 hp.
  | { kind: 'parachute'; ownerIdx: 0 | 1; rect: RectInfo }
  // АНИМ.9: Расчёт тяжёлого пулемёта — пунктирный красный луч от
  // Расчёта к attacker'у, выстрел ДО основного боя.
  | { kind: 'raschet';
      fromRect: RectInfo;
      toRect: RectInfo;
      toIsHero: boolean;
    }
  // АНИМ.11: перемещение юнита (Перегруппировка/Передислокация/Стратег/
  // Фиксер). Юнит-ghost летит из старой ячейки в новую.
  | { kind: 'move';
      snap: CardSnapshot;
      fromRect: RectInfo;
      toRect: RectInfo;
    }
  // АНИМ.11: Деус разыгрывает карту Влияния — превью карты в центре +
  // волна к target'у (если есть). Аналог playAction, но синий цвет.
  | { kind: 'influence';
      cardId: string;
      cardName: string;
      targetRect: RectInfo | null;
    }
  // АНИМ.14: stack-up — добавление Мода/стака Заражения юниту. Всплывающая
  // иконка + цифра (например "⚙ +1" / "☣ ×2"). Persistent badge на юните
  // обновится через React; анимация только подсвечивает изменение.
  | { kind: 'stackUp';
      instanceId: number;
      rect: RectInfo;
      stackKind: 'mod' | 'infected';
      count: number;  // новое значение после добавления
    }
  // АНИМ.15: keyword attach/remove flash. Короткая вспышка иконки
  // keyword'а над юнитом — игрок понимает что Молчание/Стелс/Провокация
  // наложилось или снялось.
  | { kind: 'keywordFlash';
      instanceId: number;
      rect: RectInfo;
      keyword: string;
      mode: 'add' | 'remove';
    }
  // АНИМ.15: Phase transition banner — fullscreen splash при переходе
  // Ф1→Ф2→Ф3→Ф4→Ф5. Текст по центру с fade-in/out.
  | { kind: 'phaseBanner';
      phase: number;       // 1..5
      label: string;       // "Тяга" / "Размещение" / "Бой" / "Герой 1" / "Герой 2"
    }
  // АНИМ.16: ghost карты летит из колоды в руку (player draw).
  | { kind: 'cardDraw';
      ownerIdx: 0 | 1;
      fromRect: RectInfo;
      toRect: RectInfo;
      faction: string;
    }
  // АНИМ.16: ghost карты летит из руки в slot (player placement).
  | { kind: 'cardPlacement';
      snap: CardSnapshot;
      fromRect: RectInfo;
      toRect: RectInfo;
    };

// ─── Сам Animator ─────────────────────────────────────────
export class Animator {
  skip = false;
  // Текущее overlay-событие. AnimationLayer подписан и рисует.
  current: OverlayEvent | null = null;
  // Force-update React-компонента BattleScreen, чтобы state-чипы юнитов
  // показали свежие hp/armor после мутаций engine'а между атаками.
  rerenderFn: (() => void) | null = null;

  private listeners = new Set<() => void>();

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }
  // Public — нужен replay-плееру PvP который ставит current напрямую.
  notify(): void {
    this.listeners.forEach((fn) => { try { fn(); } catch {} });
  }

  rerender(): void { if (this.rerenderFn) this.rerenderFn(); }
  shouldStop(): boolean { return this.skip; }

  // ─── DOM helpers ──────────────────────────────────────
  private rectOf(instanceId: number): RectInfo | null {
    if (!instanceId) return null;
    const el = document.querySelector(`[data-instance-id="${instanceId}"]`) as HTMLElement | null;
    if (el) {
      const r = el.getBoundingClientRect();
      const rect = { left: r.left, top: r.top, width: r.width, height: r.height };
      // Этап 80: держим кэш свежим и используем его как fallback ниже.
      this.rectCache.set(instanceId, rect);
      return rect;
    }
    // Этап 80 (фикс «анимации пропадают с ростом юнитов/ходов»): если DOM-
    // элемент не найден в этот микромомент (React не успел отрисовать
    // свежий/перемещённый юнит, layout-сдвиг), берём ПОСЛЕДНЮЮ известную
    // позицию из кэша (updateRectCache зовётся каждый render) вместо null
    // → анимация показывается, а не пропускается молча. Аналогично getRect.
    return this.rectCache.get(instanceId) || null;
  }

  // АНИМ.12: rect для любой карты (юнит или Hero). Hero в engine
  // instanceId не имеет — используем synthetic -1 (свой) / -2 (вражеский)
  // по ownerId. Юнит — обычный instanceId.
  private rectOfCard(card: any): RectInfo | null {
    if (!card) return null;
    if (card.type === 'hero') {
      const heroId = card.ownerId === 1 ? -2 : -1;
      return this.rectOf(heroId);
    }
    return this.rectOf(card.instanceId);
  }

  private snapCard(card: any): CardSnapshot {
    const isHero = card?.type === 'hero';
    return {
      instanceId: card?.instanceId ?? 0,
      id: card?.id || '',
      name_ru: card?.name_ru || card?.id || '',
      faction: card?.faction || 'deus',
      atk: card?.current?.atk ?? card?.base?.atk ?? card?.atk ?? 0,
      armor: card?.current?.armor ?? card?.base?.armor ?? card?.armor ?? 0,
      hp: card?.current?.hp ?? card?.base?.hp ?? card?.hp ?? 0,
      maxHp: card?.base?.hp ?? card?.hp ?? 0,
      isHero,
    };
  }

  private wait(ms: number): Promise<void> {
    if (this.skip || ms <= 0) return Promise.resolve();
    return new Promise((r) => setTimeout(r, ms));
  }

  // ─── Engine API ──────────────────────────────────────

  async revealCard(card: any): Promise<void> {
    if (this.skip || !card) return;
    const rect = this.rectOf(card.instanceId);
    if (rect) {
      this.current = { kind: 'reveal', instanceId: card.instanceId, rect };
      this.notify();
    }
    await this.wait(T.reveal);
    if (this.current?.kind === 'reveal' && this.current.instanceId === card?.instanceId) {
      this.current = null;
      this.notify();
    }
  }

  async beforeAttack(attacker: any, target: any): Promise<void> {
    if (this.skip || !attacker || !target) return;
    // АНИМ.12-fix: Hero (type==='hero') может иметь instanceId=undefined
    // в PvE engine (только PvP match-adapter назначает -1/-2). Используем
    // rectOfCard который для Hero ищет synthetic ID через ownerId.
    const aRect = this.rectOfCard(attacker);
    const tRect = this.rectOfCard(target);
    if (!aRect || !tRect) {
      // DOM не найден — без overlay, но движок ждёт паузу.
      await this.wait(T.telegraph + T.lunge);
      return;
    }
    const attackerSnap = this.snapCard(attacker);
    const targetSnap = this.snapCard(target);
    this.current = {
      kind: 'attack',
      attacker: attackerSnap, target: targetSnap,
      attackerRect: aRect, targetRect: tRect,
      stage: 'telegraph',
      atkDmg: null, defDmg: null,
    };
    this.notify();
    // Telegraph (приседание) → Lunge (полёт к target).
    await this.wait(T.telegraph);
    if (this.skip) return;
    if (this.current?.kind === 'attack') {
      this.current.stage = 'lunge';
      this.notify();
    }
    await this.wait(T.lunge);
  }

  async afterAttack(_a: any, _t: any, atkDmg = 0, defDmg = 0): Promise<void> {
    if (this.skip) return;
    if (this.current?.kind === 'attack') {
      this.current.stage = 'impact';
      this.current.atkDmg = atkDmg;
      this.current.defDmg = defDmg;
      this.notify();
    }
    // Impact + damage numbers + return.
    await this.wait(T.impact);
    if (this.skip) return;
    if (this.current?.kind === 'attack') {
      this.current.stage = 'return';
      this.notify();
    }
    await this.wait(T.returnMs);
    if (this.current?.kind === 'attack') {
      this.current = null;
      this.notify();
    }
  }

  async death(card: any): Promise<void> {
    if (this.skip || !card) return;
    const rect = this.rectOf(card.instanceId);
    if (!rect) {
      await this.wait(T.death);
      return;
    }
    // АНИМ.17: discard-иконка владельца юнита (data-discard="my|enemy").
    // Fallback на любой data-discard. Если не находим — null (fade на месте).
    const ownerIdx = card.ownerId ?? 0;
    const side = ownerIdx === 1 ? 'enemy' : 'my';
    let discardEl = document.querySelector(`[data-discard="${side}"]`) as HTMLElement | null;
    if (!discardEl) discardEl = document.querySelector('[data-discard]') as HTMLElement | null;
    let discardRect: RectInfo | null = null;
    if (discardEl) {
      const r = discardEl.getBoundingClientRect();
      discardRect = { left: r.left, top: r.top, width: r.width, height: r.height };
    }
    this.current = {
      kind: 'death', instanceId: card.instanceId, rect, discardRect,
      snap: this.snapCard(card),
    };
    this.notify();
    await this.wait(T.death);
    if (this.current?.kind === 'death') {
      this.current = null;
      this.notify();
    }
  }

  async heroDeath(hero: any): Promise<void> {
    if (this.skip || !hero) return;
    const rect = this.rectOfCard(hero);
    if (!rect) {
      await this.wait(T.heroDeath);
      return;
    }
    // АНИМ.12-fix: ownerIdx через hero.ownerId (engine) ИЛИ synthetic ID
    // (PvP). В PvE engine hero.instanceId undefined, но hero.ownerId есть.
    const ownerIdx: 0 | 1 = (hero.ownerId === 1 || hero.instanceId === -2) ? 1 : 0;
    this.current = { kind: 'heroDeath', ownerIdx, rect };
    this.notify();
    await this.wait(T.heroDeath);
    if (this.current?.kind === 'heroDeath') {
      this.current = null;
      this.notify();
    }
  }

  async pause(): Promise<void> {
    if (this.skip) return;
    await this.wait(T.pause);
  }

  // АНИМ.6: Hero блокирует атаку Иммунитетом.
  async heroImmune(hero: any): Promise<void> {
    if (this.skip || !hero) return;
    const rect = this.rectOfCard(hero);
    if (!rect) { await this.wait(T.immune); return; }
    const ownerIdx: 0 | 1 = (hero.ownerId === 1 || hero.instanceId === -2) ? 1 : 0;
    this.current = { kind: 'heroImmune', ownerIdx, rect };
    this.notify();
    await this.wait(T.immune);
    if (this.current?.kind === 'heroImmune') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.6: Стелс-юнит выходит из тени.
  async stealthActivate(unit: any): Promise<void> {
    if (this.skip || !unit) return;
    const rect = this.rectOf(unit.instanceId);
    if (!rect) { await this.wait(T.stealth); return; }
    this.current = { kind: 'stealthActivate', instanceId: unit.instanceId, rect };
    this.notify();
    await this.wait(T.stealth);
    if (this.current?.kind === 'stealthActivate') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.7: карта-Действие применена.
  async playAction(card: any, target?: any): Promise<void> {
    if (this.skip || !card) return;
    const targetRect = target ? this.rectOf(target.instanceId) : null;
    this.current = {
      kind: 'playAction',
      cardId: card.id || '',
      cardName: card.name_ru || card.id || '',
      faction: card.faction || 'neutral',
      targetRect,
    };
    this.notify();
    await this.wait(T.playAction);
    if (this.current?.kind === 'playAction') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.7: +HP юниту/Герою. АНИМ.12: для Hero rect через synthetic ID.
  async heal(target: any, amount: number): Promise<void> {
    if (this.skip || !target || amount <= 0) return;
    const rect = this.rectOfCard(target);
    if (!rect) { await this.wait(T.heal); return; }
    this.current = { kind: 'heal', instanceId: target.instanceId ?? 0, rect, amount };
    this.notify();
    await this.wait(T.heal);
    if (this.current?.kind === 'heal') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.7: ±ATK/±ARM/±HP юниту/Герою. АНИМ.12: Hero через rectOfCard.
  // АНИМ.13: поддержка отрицательных значений (debuff'ы).
  async buff(target: any, dAtk: number, dArmor: number, dHp: number): Promise<void> {
    if (this.skip || !target) return;
    if (dAtk === 0 && dArmor === 0 && dHp === 0) return;
    const rect = this.rectOfCard(target);
    if (!rect) { await this.wait(T.buff); return; }
    this.current = { kind: 'buff', instanceId: target.instanceId ?? 0, rect, dAtk, dArmor, dHp };
    this.notify();
    await this.wait(T.buff);
    if (this.current?.kind === 'buff') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.7: призыв юнита.
  async summon(unit: any): Promise<void> {
    if (this.skip || !unit) return;
    const rect = this.rectOf(unit.instanceId);
    if (!rect) { await this.wait(T.summon); return; }
    this.current = { kind: 'summon', instanceId: unit.instanceId, rect };
    this.notify();
    await this.wait(T.summon);
    if (this.current?.kind === 'summon') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.7: перенаправление атаки.
  async redirect(from: any, to: any, reason: 'sekretar' | 'taunt'): Promise<void> {
    if (this.skip || !from || !to) return;
    const fromRect = this.rectOf(from.instanceId);
    const toRect = this.rectOf(to.instanceId);
    if (!fromRect || !toRect) { await this.wait(T.redirectArrow); return; }
    this.current = { kind: 'redirect', fromRect, toRect, reason };
    this.notify();
    await this.wait(T.redirectArrow);
    if (this.current?.kind === 'redirect') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.8: Парашют — Hero спасается от смертельного удара.
  async parachute(hero: any): Promise<void> {
    if (this.skip || !hero) return;
    const rect = this.rectOfCard(hero);
    if (!rect) { await this.wait(T.parachute); return; }
    const ownerIdx: 0 | 1 = (hero.ownerId === 1 || hero.instanceId === -2) ? 1 : 0;
    this.current = { kind: 'parachute', ownerIdx, rect };
    this.notify();
    await this.wait(T.parachute);
    if (this.current?.kind === 'parachute') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.11: lookup rect по data-side/data-line/data-slot.
  // ownerIdx 0 = my (data-side="my"), 1 = enemy (data-side="enemy").
  private rectOfSlot(ownerIdx: 0 | 1, line: number, slot: number): RectInfo | null {
    const side = ownerIdx === 0 ? 'my' : 'enemy';
    const el = document.querySelector(
      `[data-side="${side}"][data-line="${line}"][data-slot="${slot}"]`
    ) as HTMLElement | null;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height };
  }

  // АНИМ.16: card draw — ghost карты летит из колоды в руку.
  async cardDraw(ownerIdx: 0 | 1, faction = 'neutral'): Promise<void> {
    if (this.skip) return;
    const side = ownerIdx === 0 ? 'my' : 'enemy';
    const deckEl = document.querySelector(`[data-deck="${side}"]`) as HTMLElement | null;
    if (!deckEl) { await this.wait(T.cardDraw); return; }
    const dr = deckEl.getBoundingClientRect();
    const fromRect: RectInfo = { left: dr.left, top: dr.top, width: dr.width, height: dr.height };
    // toRect — своя hand-зона; для врага нет hand-bar, лёгкий dropoff
    // вниз от deck'а как indicator.
    let toRect: RectInfo;
    if (ownerIdx === 0) {
      const handEl = document.querySelector('[data-hand="my"]') as HTMLElement | null;
      if (handEl) {
        const r = handEl.getBoundingClientRect();
        toRect = { left: r.left + r.width / 2 - 30, top: r.top, width: 60, height: 80 };
      } else {
        toRect = { left: fromRect.left, top: fromRect.top + 80, width: fromRect.width, height: 60 };
      }
    } else {
      toRect = { left: fromRect.left, top: fromRect.top + 60, width: fromRect.width, height: 50 };
    }
    this.current = { kind: 'cardDraw', ownerIdx, fromRect, toRect, faction };
    this.notify();
    await this.wait(T.cardDraw);
    if (this.current?.kind === 'cardDraw') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.16: card placement — ghost летит из руки в slot.
  async cardPlacement(unit: any, line: number, slot: number, ownerIdx?: 0 | 1): Promise<void> {
    if (this.skip || !unit) return;
    const idx: 0 | 1 = (ownerIdx ?? unit.ownerId ?? 0) as 0 | 1;
    const side = idx === 0 ? 'my' : 'enemy';
    // fromRect: своя hand-зона или deck врага.
    let fromRect: RectInfo | null = null;
    if (idx === 0) {
      const handEl = document.querySelector('[data-hand="my"]') as HTMLElement | null;
      if (handEl) {
        const r = handEl.getBoundingClientRect();
        fromRect = { left: r.left + r.width / 2 - 30, top: r.top, width: 60, height: 80 };
      }
    } else {
      const deckEl = document.querySelector('[data-deck="enemy"]') as HTMLElement | null;
      if (deckEl) {
        const r = deckEl.getBoundingClientRect();
        fromRect = { left: r.left, top: r.top, width: r.width, height: r.height };
      }
    }
    const slotEl = document.querySelector(
      `[data-side="${side}"][data-line="${line}"][data-slot="${slot}"]`
    ) as HTMLElement | null;
    if (!fromRect || !slotEl) { await this.wait(T.cardPlacement); return; }
    const sr = slotEl.getBoundingClientRect();
    const toRect: RectInfo = { left: sr.left, top: sr.top, width: sr.width, height: sr.height };
    this.current = { kind: 'cardPlacement', snap: this.snapCard(unit), fromRect, toRect };
    this.notify();
    await this.wait(T.cardPlacement);
    if (this.current?.kind === 'cardPlacement') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.15: keyword attach/remove flash.
  async keywordFlash(unit: any, keyword: string, mode: 'add' | 'remove'): Promise<void> {
    if (this.skip || !unit || !keyword) return;
    const rect = this.rectOfCard(unit);
    if (!rect) { await this.wait(T.keywordFlash); return; }
    this.current = {
      kind: 'keywordFlash',
      instanceId: unit.instanceId ?? 0,
      rect, keyword, mode,
    };
    this.notify();
    await this.wait(T.keywordFlash);
    if (this.current?.kind === 'keywordFlash') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.15: Phase transition banner — fullscreen splash.
  async phaseBanner(phase: number, label: string): Promise<void> {
    if (this.skip) return;
    this.current = { kind: 'phaseBanner', phase, label };
    this.notify();
    await this.wait(T.phaseBanner);
    if (this.current?.kind === 'phaseBanner') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.14: stack-up индикатор для Mod / Infected count++.
  async stackUp(unit: any, kind: 'mod' | 'infected', count: number): Promise<void> {
    if (this.skip || !unit) return;
    const rect = this.rectOfCard(unit);
    if (!rect) { await this.wait(T.stackUp); return; }
    this.current = {
      kind: 'stackUp',
      instanceId: unit.instanceId ?? 0,
      rect,
      stackKind: kind,
      count,
    };
    this.notify();
    await this.wait(T.stackUp);
    if (this.current?.kind === 'stackUp') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.11: перемещение юнита между ячейками.
  async move(
    unit: any, _fromLine: number, _fromSlot: number,
    _toLine: number, _toSlot: number, ownerIdx?: 0 | 1
  ): Promise<void> {
    if (this.skip || !unit) return;
    // ownerIdx из аргумента, иначе из unit.ownerId (engine), иначе 0.
    const idx: 0 | 1 = (ownerIdx ?? unit.ownerId ?? 0) as 0 | 1;
    const fromRect = this.rectOfSlot(idx, _fromLine, _fromSlot);
    // toRect — текущая позиция юнита (DOM уже обновлён). Если slot изменился —
    // обновлённый rect через data-instance-id.
    const toRect = this.rectOf(unit.instanceId) || this.rectOfSlot(idx, _toLine, _toSlot);
    if (!fromRect || !toRect) { await this.wait(T.move); return; }
    this.current = { kind: 'move', snap: this.snapCard(unit), fromRect, toRect };
    this.notify();
    await this.wait(T.move);
    if (this.current?.kind === 'move') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.11: Деус разыгрывает карту Влияния.
  async influence(card: any, target?: any): Promise<void> {
    if (this.skip || !card) return;
    const targetRect = target ? this.rectOf(target.instanceId) : null;
    this.current = {
      kind: 'influence',
      cardId: card.id || '',
      cardName: card.name_ru || card.id || '',
      targetRect,
    };
    this.notify();
    await this.wait(T.influence);
    if (this.current?.kind === 'influence') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.9: Расчёт стреляет ДО основного боя.
  async raschet(from: any, to: any): Promise<void> {
    if (this.skip || !from || !to) return;
    const fromRect = this.rectOf(from.instanceId);
    const toRect = this.rectOf(to.instanceId);
    if (!fromRect || !toRect) { await this.wait(T.raschet); return; }
    const toIsHero = to.type === 'hero';
    this.current = { kind: 'raschet', fromRect, toRect, toIsHero };
    this.notify();
    await this.wait(T.raschet);
    if (this.current?.kind === 'raschet') {
      this.current = null;
      this.notify();
    }
  }

  // АНИМ.7: урон по юниту без attacker'а. АНИМ.12: Hero через rectOfCard.
  async damage(target: any, amount: number): Promise<void> {
    if (this.skip || !target || amount <= 0) return;
    const rect = this.rectOfCard(target);
    if (!rect) { await this.wait(500); return; }
    const isHero = target.type === 'hero';
    this.current = { kind: 'damage', instanceId: target.instanceId ?? 0, rect, amount, isHero };
    this.notify();
    await this.wait(500);
    if (this.current?.kind === 'damage') {
      this.current = null;
      this.notify();
    }
  }

  // ─── Cleanup API ─────────────────────────────────────

  // Используется engine'ом когда attack failed (Авангард/Иммунитет/etc)
  // и attack_start был выставлен, но afterAttack не зовётся.
  rewindAttack(): void {
    if (this.current?.kind === 'attack') {
      this.current = null;
      this.notify();
    }
  }

  // Принудительный полный сброс — для emergency (watchdog, ручная разморозка).
  reset(): void {
    this.current = null;
    this.skip = false;
    this.notify();
  }

  // ─── PvP replay queue ─────────────────────────────────
  //
  // Если bot быстро делает несколько атак подряд, сервер шлёт batch'и
  // events с разными tick'ами. Раньше каждый запускал playCombatEvents
  // параллельно → они дрались за this.current → анимации мерцали.
  // Решение: очередь. enqueueBatch добавляет, worker играет
  // последовательно через playerFn.

  private queue: string[][] = [];
  private workerRunning = false;
  private playerFn: ((raw: string[], anim: Animator) => Promise<void>) | null = null;
  // АНИМ.7: callback после опустошения очереди (для MatchOver gate —
  // отложить EndOfMatchModal до окончания анимации смерти).
  private afterIdleCb: (() => void) | null = null;

  // Запланировать callback после окончания всех очередей. Если очередь
  // уже пуста — зовём синхронно.
  scheduleAfterIdle(cb: () => void): void {
    if (this.queue.length === 0 && !this.workerRunning) {
      try { cb(); } catch (e) { console.warn('[Animator] afterIdle cb crash:', e); }
    } else {
      this.afterIdleCb = cb;
    }
  }

  setPlayer(fn: (raw: string[], anim: Animator) => Promise<void>): void {
    this.playerFn = fn;
  }

  enqueueBatch(raw: string[]): void {
    this.queue.push(raw);
    // АНИМ.4-fix-skip-ahead: если очередь длиннее 3 — дропаем старые,
    // оставляем только последний. Это снимает отставание анимации от
    // реального state'а (после быстрых атак подряд сервер генерит ticks
    // быстрее чем мы их играем; копится очередь). Лучше пропустить
    // промежуточные анимации чем отставать на 10+ сек.
    if (this.queue.length > 3) {
      const dropped = this.queue.length - 1;
      this.queue = [this.queue[this.queue.length - 1]];
      console.log(`[Animator] queue overflow, dropped ${dropped} stale batches`);
    }
    if (!this.workerRunning) {
      this.workerRunning = true;
      this.processQueue().catch((e) => {
        console.warn('[Animator] worker crash:', e);
        this.workerRunning = false;
      });
    }
  }

  private async processQueue(): Promise<void> {
    while (this.queue.length > 0) {
      const batch = this.queue.shift()!;
      if (!this.playerFn) {
        console.warn('[Animator] playerFn not set, dropping batch');
        continue;
      }
      try {
        await this.playerFn(batch, this);
      } catch (e) {
        console.warn('[Animator] batch crash:', e);
      }
    }
    this.workerRunning = false;
    // АНИМ.7: зовём scheduled-after-idle callback (MatchOver gate).
    const cb = this.afterIdleCb;
    this.afterIdleCb = null;
    if (cb) {
      try { cb(); } catch (e) { console.warn('[Animator] afterIdle cb crash:', e); }
    }
  }

  // ─── Rect-кэш ────────────────────────────────────────
  // АНИМ.4-fix-rect-cache: глобальный кэш позиций юнитов. Обновляется
  // BattleScreen на каждом render через useEffect. findRect возвращает
  // последнюю известную позицию для умерших юнитов (которых уже нет в DOM).
  private rectCache = new Map<number, RectInfo>();

  updateRectCache(): void {
    document.querySelectorAll('[data-instance-id]').forEach((el) => {
      const idStr = (el as HTMLElement).dataset.instanceId || '0';
      const id = parseInt(idStr, 10);
      if (!Number.isFinite(id) || id === 0) return;
      const r = (el as HTMLElement).getBoundingClientRect();
      this.rectCache.set(id, { left: r.left, top: r.top, width: r.width, height: r.height });
    });
  }

  // Сначала ищем в актуальном DOM (для свежевышедших юнитов), fallback на
  // кэш (для умерших, которых React уже удалил из DOM). Если ни там ни
  // там — возвращаем null (overlay пропускается, без анимации). Это
  // нормально для карт-Действий, instanceId которых может попасть в
  // event но они никогда не рендерятся как юнит на поле.
  getRect(instanceId: number): RectInfo | null {
    if (!instanceId) return null;
    const el = document.querySelector(`[data-instance-id="${instanceId}"]`) as HTMLElement | null;
    if (el) {
      const r = el.getBoundingClientRect();
      const rect = { left: r.left, top: r.top, width: r.width, height: r.height };
      this.rectCache.set(instanceId, rect);
      return rect;
    }
    return this.rectCache.get(instanceId) || null;
  }
}
